import { DestroyRef, Directive, DoCheck, HostListener, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgModel, ValidationErrors } from '@angular/forms';
import { Store, createActionGroup, props } from '@ngrx/store';
import { EMPTY, merge } from 'rxjs';

import { ActionSource } from 'src/app/state/action-sources';

import { type ControlMeta, type ControlMetaStatus } from './new-opportunity.state';

export const ControlMetaActions = createActionGroup({
  source: ActionSource.CONTROL_META,
  events: {
		'Control Meta Updated': props<{ field: string; meta: ControlMeta }>(),
		'Control Meta Cleared': props<{ field: string }>(),
  }
});

@Directive({
    selector: '[appControlMeta]',
    standalone: true,
})
export class ControlMetaDirective implements OnInit, OnDestroy, DoCheck {

    private fieldKey: string | null = null;
    private lastSnapshot: ControlMeta | null = null;
    private hasWarnedMissingNgModel = false;

    @Input()
    set appControlMeta(field: string | null | undefined) {
        const nextField = typeof field === 'string' && field.trim() ? field : null;

        if (this.fieldKey && this.fieldKey !== nextField) {
            this.store.dispatch(ControlMetaActions.controlMetaCleared({ field: this.fieldKey }));
            this.lastSnapshot = null;
        }

        this.fieldKey = nextField;
        this.dispatchSnapshotIfChanged();
    }

    constructor(
      private store: Store,
      private destroyRef: DestroyRef,
      @Optional() @Self() private ngModel?: NgModel,
    ) {}

    ngOnInit(): void {
        if (!this.ngModel) {
            this.warnMissingNgModel();
            return;
        }

        this.dispatchSnapshotIfChanged();

        merge(
            this.ngModel.statusChanges ?? EMPTY,
            this.ngModel.valueChanges ?? EMPTY,
        ).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => {
            this.dispatchSnapshotIfChanged();
        });
    }

    ngDoCheck(): void {
        // Some controls are marked as touched programmatically after mount, which
        // does not reliably emit through valueChanges/statusChanges.
        this.dispatchSnapshotIfChanged();
    }

    @HostListener('focusout')
    onFocusOut(): void {
        this.dispatchSnapshotIfChanged();
    }

    ngOnDestroy(): void {
        if (this.fieldKey) {
            this.store.dispatch(ControlMetaActions.controlMetaCleared({ field: this.fieldKey }));
        }
    }

    private dispatchSnapshotIfChanged(): void {
        if (!this.ngModel) {
            this.warnMissingNgModel();
            return;
        }

        if (!this.fieldKey) {
            return;
        }

        const nextSnapshot = this.buildSnapshot(this.ngModel);

        if (this.isSameSnapshot(this.lastSnapshot, nextSnapshot)) {
            return;
        }

        this.lastSnapshot = nextSnapshot;

        this.store.dispatch(ControlMetaActions.controlMetaUpdated({
            field: this.fieldKey,
            meta: nextSnapshot,
        }));
    }

    private buildSnapshot(ngModel: NgModel): ControlMeta {
        const status = this.mapStatus(ngModel.status ?? ngModel.control?.status);

        return {
            dirty: Boolean(ngModel.dirty),
            touched: Boolean(ngModel.touched),
            status,
            invalid: status === 'INVALID',
            errors: this.serializeErrors(ngModel.errors ?? ngModel.control?.errors),
            disabled: Boolean(ngModel.disabled ?? ngModel.control?.disabled),
            lastUpdated: Date.now(),
        };
    }

    private mapStatus(status: string | null | undefined): ControlMetaStatus {
        switch (status) {
            case 'INVALID':
            case 'PENDING':
            case 'DISABLED':
            case 'VALID':
                return status;
            default:
                return 'VALID';
        }
    }

    private serializeErrors(errors: ValidationErrors | null | undefined): Record<string, any> | null {
        if (!errors) {
            return null;
        }

        try {
            return JSON.parse(JSON.stringify(errors));
        } catch (_error) {
            return null;
        }
    }

    private isSameSnapshot(previous: ControlMeta | null, next: ControlMeta): boolean {
        if (!previous) {
            return false;
        }

        return previous.dirty === next.dirty
            && previous.touched === next.touched
            && previous.status === next.status
            && previous.invalid === next.invalid
            && previous.disabled === next.disabled
            && this.isSameErrors(previous.errors, next.errors);
    }

    private isSameErrors(
        previousErrors: Record<string, any> | null,
        nextErrors: Record<string, any> | null,
    ): boolean {
        if (previousErrors === nextErrors) {
            return true;
        }

        if (!previousErrors || !nextErrors) {
            return false;
        }

        return JSON.stringify(previousErrors) === JSON.stringify(nextErrors);
    }

    private warnMissingNgModel(): void {
        if (this.hasWarnedMissingNgModel) {
            return;
        }

        console.warn('[appControlMeta] requires NgModel on the same element.');
        this.hasWarnedMissingNgModel = true;
    }

}
